import { useState, useEffect } from 'react'
import { Card, Spin, Empty, Radio, Segmented } from 'antd'
import { ComposedChart, BarChart, Bar, PieChart, Pie, Cell, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts'
import { getStockQuarterlyFinancials } from '@/api'
import { getStockRevenueBreakdown } from '@/api/stock'
import type { StockFinancialData, StockRevenueBreakdownResponse } from '@/types/stockPool'

function fmt(val: number | null | undefined, decimals = 1, suffix = ''): string {
    if (val == null || !isFinite(val as number)) return '--'
    return (val as number).toFixed(decimals) + suffix
}

function toHundredMillion(val: number | null | undefined): number | null {
    if (val == null || !isFinite(val as number)) return null
    return +((val as number) / 1e8).toFixed(2)
}

function fmtLarge(val: number | null | undefined): string {
    if (val == null || !isFinite(val as number)) return '--'
    const n = val as number
    if (Math.abs(n) >= 1e8) return (n / 1e8).toFixed(1) + '亿'
    if (Math.abs(n) >= 1e4) return (n / 1e4).toFixed(1) + '万'
    return n.toFixed(0)
}

interface Props {
    stockCode: string
    financials: StockFinancialData[]
}

type ViewMode = 'annual' | 'quarterly'
type StatementTab = 'income' | 'balance' | 'cashflow'

const INCOME_ROWS: { label: string; key: keyof StockFinancialData; fmt: (v: any) => string }[] = [
    { label: '营业收入', key: 'revenue', fmt: fmtLarge },
    { label: '毛利润', key: 'gross_profit', fmt: fmtLarge },
    { label: '毛利率', key: 'gross_margin_pct', fmt: v => fmt(v, 1, '%') },
    { label: '净利润（归母）', key: 'net_profit_parent', fmt: fmtLarge },
    { label: '净利率', key: 'net_margin_pct', fmt: v => fmt(v, 1, '%') },
    { label: '调整后净利润', key: 'adjusted_net_profit_non_ifrs', fmt: fmtLarge },
    { label: '同比增速', key: 'adjusted_net_profit_yoy_pct', fmt: v => fmt(v, 1, '%') },
]

const BALANCE_ROWS: { label: string; key: keyof StockFinancialData; fmt: (v: any) => string }[] = [
    { label: '总资产', key: 'total_assets', fmt: fmtLarge },
    { label: '货币资金', key: 'cash_and_equivalents', fmt: fmtLarge },
    { label: '总负债', key: 'total_liabilities', fmt: fmtLarge },
    { label: '资产负债率', key: 'debt_to_asset_ratio_pct', fmt: v => fmt(v, 1, '%') },
    { label: '股东权益', key: 'net_assets_equity', fmt: fmtLarge },
    { label: 'ROE', key: 'roe_pct', fmt: v => fmt(v, 1, '%') },
    { label: 'ROA', key: 'roa_pct', fmt: v => fmt(v, 1, '%') },
]

const CASHFLOW_ROWS: { label: string; key: keyof StockFinancialData; fmt: (v: any) => string }[] = [
    { label: '经营活动现金流', key: 'operating_cash_flow_cfo', fmt: fmtLarge },
    { label: '投资活动现金流', key: 'investing_cash_flow_cfi', fmt: fmtLarge },
    { label: '融资活动现金流', key: 'financing_cash_flow_cff', fmt: fmtLarge },
    { label: '自由现金流', key: 'free_cash_flow_fcf', fmt: fmtLarge },
]

type RevDimension = 1 | 2 | 4 | 8
const DIM_LABELS: Record<RevDimension, string> = { 1: '产品', 2: '行业', 4: '地区', 8: '业务' }
const PIE_COLORS = ['#1677ff', '#52c41a', '#faad14', '#ff4d4f', '#7c3aed', '#13c2c2', '#eb2f96', '#fa8c16']

export default function FinancialsTab({ stockCode, financials }: Props) {
    const [viewMode, setViewMode] = useState<ViewMode>('annual')
    const [quarterlyItems, setQuarterlyItems] = useState<StockFinancialData[]>([])
    const [loading, setLoading] = useState(false)
    const [statTab, setStatTab] = useState<StatementTab>('income')
    const [revBreakdown, setRevBreakdown] = useState<StockRevenueBreakdownResponse | null>(null)
    const [revDim, setRevDim] = useState<RevDimension>(1)

    useEffect(() => {
        setQuarterlyItems([])
        if (viewMode !== 'quarterly' || !stockCode) return
        setLoading(true)
        getStockQuarterlyFinancials({ code: stockCode, page_size: 12 })
            .then(r => setQuarterlyItems(r.items))
            .catch(() => setQuarterlyItems([]))
            .finally(() => setLoading(false))
    }, [stockCode, viewMode])

    useEffect(() => {
        if (!stockCode) return
        setRevBreakdown(null)
        getStockRevenueBreakdown(stockCode)
            .then(setRevBreakdown)
            .catch(() => setRevBreakdown(null))
    }, [stockCode])

    const source = viewMode === 'annual' ? financials : quarterlyItems
    const displayItems = source.slice(0, 8)
    const tableItems = source.slice(0, 5)

    const chartData = [...displayItems].reverse().map(d => ({
        period: d.fiscal_year ?? d.closing_date?.slice(0, 7) ?? '',
        revenue: toHundredMillion(d.revenue),
        netProfit: toHundredMillion(d.net_profit_parent),
        grossMargin: d.gross_margin_pct ?? null,
        netMargin: d.net_margin_pct ?? null,
        revenueYoY: d.revenue_yoy_pct ?? null,
    }))

    const tableHeaders = tableItems.map(d => d.fiscal_year ?? d.closing_date?.slice(0, 7) ?? '')

    const statRows = statTab === 'income' ? INCOME_ROWS
        : statTab === 'balance' ? BALANCE_ROWS
        : CASHFLOW_ROWS

    return (
        <div className="new-kb-tab financials-tab">
            <div className="tab-toolbar">
                <Radio.Group
                    value={viewMode}
                    onChange={e => setViewMode(e.target.value)}
                    size="small"
                    optionType="button"
                    options={[
                        { label: '年度', value: 'annual' },
                        { label: '季度', value: 'quarterly' },
                    ]}
                />
            </div>

            {loading ? (
                <div className="chart-placeholder"><Spin /></div>
            ) : source.length === 0 ? (
                <Empty description="暂无财务数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <>
                    <Card title="营收与净利润（亿）" size="small" className="new-kb-card">
                        <ResponsiveContainer width="100%" height={200}>
                            <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                                <YAxis yAxisId="amt" tick={{ fontSize: 11 }} width={52} />
                                <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 11 }} width={44} unit="%" />
                                <Tooltip
                                    contentStyle={{ fontSize: 12 }}
                                    formatter={(v, name) => {
                                        if (name === '营收同比') return [typeof v === 'number' ? v.toFixed(1) + '%' : v, name]
                                        return [v, name]
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar yAxisId="amt" dataKey="revenue" name="营收" fill="#1677ff" radius={[2, 2, 0, 0]} />
                                <Bar yAxisId="amt" dataKey="netProfit" name="净利润" fill="#52c41a" radius={[2, 2, 0, 0]} />
                                <Line yAxisId="pct" type="monotone" dataKey="revenueYoY" name="营收同比" stroke="#faad14" dot={{ r: 3 }} strokeWidth={2} connectNulls />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card title="利润率（%）" size="small" className="new-kb-card">
                        <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} width={40} unit="%" />
                                <Tooltip
                                    contentStyle={{ fontSize: 12 }}
                                    formatter={(v) => [typeof v === 'number' ? v.toFixed(1) + '%' : v, '']}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="grossMargin" name="毛利率" fill="#faad14" radius={[2, 2, 0, 0]} />
                                <Bar dataKey="netMargin" name="净利率" fill="#13c2c2" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card
                        title={
                            <Segmented
                                size="small"
                                value={statTab}
                                onChange={v => setStatTab(v as StatementTab)}
                                options={[
                                    { label: '利润表', value: 'income' },
                                    { label: '资产负债表', value: 'balance' },
                                    { label: '现金流量表', value: 'cashflow' },
                                ]}
                            />
                        }
                        size="small"
                        className="new-kb-card"
                    >
                        <div className="fin-table-wrap">
                            <table className="fin-table">
                                <thead>
                                    <tr>
                                        <th>指标</th>
                                        {tableHeaders.map(h => <th key={h}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {statRows.map(row => (
                                        <tr key={row.key as string}>
                                            <td className="fin-table-label">{row.label}</td>
                                            {tableItems.map((d, i) => (
                                                <td key={i}>{row.fmt(d[row.key])}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    {/* 分部营收 */}
                    {(() => {
                        const item = revBreakdown?.item ?? null
                        const availableDims = item ? item.breakdown_list.map(b => b.type as RevDimension) : []
                        const activeDim: RevDimension = availableDims.includes(revDim) ? revDim : (availableDims[0] ?? 1)
                        const breakdown = item?.breakdown_list.find(b => b.type === activeDim)
                        const pieData = (breakdown?.item_list ?? [])
                            .filter(e => e.main_oper_income != null)
                            .map(e => ({ name: e.name, value: e.main_oper_income! }))
                        return (
                            <Card
                                title={item ? `主营业务营收构成（${item.period ?? ''}）` : '主营业务营收构成'}
                                size="small"
                                className="new-kb-card"
                                extra={
                                    availableDims.length > 1 && (
                                        <Radio.Group
                                            value={activeDim}
                                            onChange={e => setRevDim(e.target.value)}
                                            size="small"
                                            optionType="button"
                                            options={availableDims.map(d => ({ label: DIM_LABELS[d], value: d }))}
                                        />
                                    )
                                }
                            >
                                {!item ? (
                                    <Empty description="暂无分部营收数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                                ) : pieData.length === 0 ? (
                                    <Empty description="暂无构成数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                                ) : (
                                    <>
                                        <ResponsiveContainer width="100%" height={180}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={45}
                                                    outerRadius={75}
                                                    label={false}
                                                    labelLine={false}
                                                >
                                                    {pieData.map((_, idx) => (
                                                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{ fontSize: 12 }}
                                                    formatter={(v) => [fmtLarge(typeof v === 'number' ? v : null), '收入']}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="fin-table-wrap" style={{ marginTop: 4 }}>
                                            <table className="fin-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ textAlign: 'left' }}></th>
                                                        <th>业务名称</th>
                                                        <th>收入</th>
                                                        <th>占比</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pieData.map((row, idx) => {
                                                        const total = pieData.reduce((s, r) => s + r.value, 0)
                                                        const pct = total > 0 ? (row.value / total * 100).toFixed(1) : '--'
                                                        return (
                                                            <tr key={idx}>
                                                                <td style={{ textAlign: 'center', width: 12 }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        width: 10, height: 10,
                                                                        borderRadius: 2,
                                                                        background: PIE_COLORS[idx % PIE_COLORS.length],
                                                                    }} />
                                                                </td>
                                                                <td className="fin-table-label">{row.name}</td>
                                                                <td>{fmtLarge(row.value)}</td>
                                                                <td>{pct}%</td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </Card>
                        )
                    })()}
                </>
            )}
        </div>
    )
}
